import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Smartphone, Mail, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';

export default function ContactPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/Contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          message: message.trim(),
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message ||
          data?.title ||
          'تعذر إرسال الرسالة. يرجى المحاولة مرة أخرى.'
        );
      }

      setSubmitted(true);
      setName('');
      setPhone('');
      setEmail('');
      setMessage('');
    } catch (err) {
      console.error('Contact form error:', err);

      setError(
        err instanceof Error
          ? err.message
          : 'تعذر إرسال الرسالة. يرجى المحاولة مرة أخرى.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // تم إضافة flex flex-col لضمان توزيع المساحة بشكل صحيح
    <div className="flex flex-col min-h-screen bg-slate-50" dir="rtl">
      
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-slate-50/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo-waigh-transparent.png" alt="Target Express Logo" className="h-20 w-auto object-contain hover:scale-105 transition-transform duration-300 bg-transparent" />
            <div className="hidden sm:block">
              <span className="text-xl font-bold text-gray-900 block leading-tight">Target Express</span>
              <span className="text-[10px] text-gray-500 block leading-tight">Shipping, Logistics &amp; Fulfillment Solutions</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')}>تسجيل الدخول</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => navigate('/register')}>
              ابدأ الشحن الآن
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-6">
            <Mail className="w-8 h-8" />
          </div>
          <h1 className="text-4xl font-bold mb-4">اتصل بنا</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            فريقنا جاهز للرد على استفساراتك ومساعدتك في أي وقت.
          </p>
        </div>
      </section>

      {/* Content (تم إضافة flex-grow لدفع الفوتر للأسفل إذا كان المحتوى قصيراً) */}
      <section className="py-20 flex-grow">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">معلومات التواصل</h2>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">العنوان</p>
                  <p className="font-semibold text-gray-900">القاهرة، مصر</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">الهاتف</p>
                  <p className="font-semibold text-gray-900">01008524717</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">البريد الإلكتروني</p>
                  <p className="font-semibold text-gray-900">support@targetexpress.com</p>
                </div>
              </div>
            </div>
          </div>

          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">تم استلام رسالتك</h3>
                  <p className="text-gray-600">سيتواصل معك فريقنا في أقرب وقت ممكن.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="الاسم بالكامل"
                    required
                    disabled={submitting}
                  />

                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="رقم الهاتف"
                    required
                    disabled={submitting}
                  />

                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="البريد الإلكتروني"
                    required
                    disabled={submitting}
                  />

                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="رسالتك"
                    rows={5}
                    required
                    disabled={submitting}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />

                  {error && (
                    <div
                      role="alert"
                      className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
                    >
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 py-6 text-lg font-semibold"
                  >
                    <Send className="w-4 h-4 ml-2" />
                    {submitting ? 'جارٍ إرسال الرسالة...' : 'إرسال الرسالة'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer (تم إضافة mt-auto لضمان بقائه في الأسفل دائماً) */}
      <footer className="bg-blue-950 text-gray-400 py-12 border-t border-slate-800 mt-auto">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/icon-512-from-server.png" alt="Target Express" className="h-24 w-auto object-contain" />
                <div>
                  <span className="text-lg font-bold text-white block leading-tight">Target Express</span>
                  <span className="text-[10px] text-gray-400 block leading-tight">Shipping, Logistics &amp; Fulfillment Solutions</span>
                </div>
              </div>
              <p className="text-sm leading-relaxed">شريكك اللوجستي الموثوق في مصر. نوفر حلول شحن مبتكرة وسريعة لتنمية أعمالك.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">روابط سريعة</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/about" className="hover:text-orange-500 transition">عن الشركة</a></li>
                <li><a href="/#features" className="hover:text-orange-500 transition">خدماتنا</a></li>
                <li><a href="/pricing" className="hover:text-orange-500 transition">الأسعار</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">الدعم</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/support" className="hover:text-orange-500 transition">مركز المساعدة</a></li>
                <li><a href="/track" className="hover:text-orange-500 transition">تتبع شحنتك</a></li>
                <li><a href="/contact" className="hover:text-orange-500 transition">اتصل بنا</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">تواصل معنا</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> القاهرة، مصر</li>
                <li className="flex items-center gap-2"><Smartphone className="w-4 h-4" /> 01008524717</li>
                <li className="flex items-center gap-2">support@targetexpress.com</li>
                <li><a href="https://www.facebook.com/targetexpress" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition">صفحتنا على فيسبوك</a></li>
                <li><a href="https://m.me/targetexpress" target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 transition">كلّمنا على ماسنجر</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            © {new Date().getFullYear()} Target Express. جميع الحقوق محفوظة.
          </div>
        </div>
      </footer>
    </div>
  );
}